import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Input,
  Checkbox,
  Button,
  VStack,
  HStack,
  Grid,
  GridItem,
  Textarea,
  FormControl,
  FormLabel,
  Divider,
  useToast,
  Image,
} from "@chakra-ui/react";
import { FaDownload, FaPrint } from "react-icons/fa";
import { generateEOIPDF } from "./components/generateEOIPDF";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

export interface EOIFormData {
  // Top section
  date: string;
  salesChannel: string;
  bdm: string;

  // Client Details
  firstNameClientA: string;
  middleNameClientA: string;
  lastNameClientA: string;
  firstNameClientB: string;
  middleNameClientB: string;
  lastNameClientB: string;
  address: string;
  phonePrimary: string;
  phoneSecondary: string;
  emailPrimary: string;
  emailSecondary: string;
  purchasingEntity: string;
  customerTypeBuyer: boolean;
  customerTypeOwnerOccupier: boolean;
  customerTypeInvestor: boolean;
  customerTypeSMSF: boolean;

  // Property Details
  estateName: string;
  stage: string;
  lotNumber: string;
  street: string;
  houseDesign: string;
  facade: string;
  landSize: string;
  buildPrice: string;
  landPrice: string;
  totalPrice: string;
  landRegisteredYes: boolean;
  landRegisteredExpectedDate: string;
  propertyPurchasedYes: boolean;
  propertyPurchasedEOI: boolean;

  // Solicitor & Finance
  solicitorCompany: string;
  solicitorContact: string;
  solicitorPhone: string;
  solicitorEmail: string;
  solicitorAddress: string;
  financeCompany: string;
  financeContact: string;
  financePhone: string;
  financeEmail: string;

  // Contract Details
  contractsImmediatelyYes: boolean;
  contractsImmediatelyNo: boolean;
  contractsSentToBuyerSolicitor: boolean;
  contractsSentToAgent: boolean;
  contractsViaDocuSign: boolean;
  contractsViaEmail: boolean;
  ownershipPercentClientA: string;
  ownershipPercentClientB: string;
  subjectToFinanceYes: boolean;
  subjectToFinanceNo: boolean;
  depositDueDate14Days: boolean;
  depositDueDate21Days: boolean;
  depositDueDate28Days: boolean;
  depositDueDateCustomDays: string;
  specialConditionsYes: boolean;
  specialConditionsNo: boolean;
  specialConditionsDetails: string;
}

const EOIForm: React.FC = () => {
  const toast = useToast();
  const [formData, setFormData] = useState<EOIFormData>({
    date: "",
    salesChannel: "",
    bdm: "",
    firstNameClientA: "",
    middleNameClientA: "",
    lastNameClientA: "",
    firstNameClientB: "",
    middleNameClientB: "",
    lastNameClientB: "",
    address: "",
    phonePrimary: "",
    phoneSecondary: "",
    emailPrimary: "",
    emailSecondary: "",
    purchasingEntity: "",
    customerTypeBuyer: false,
    customerTypeOwnerOccupier: false,
    customerTypeInvestor: false,
    customerTypeSMSF: false,
    estateName: "",
    stage: "",
    lotNumber: "",
    street: "",
    houseDesign: "",
    facade: "",
    landSize: "",
    buildPrice: "",
    landPrice: "",
    totalPrice: "",
    landRegisteredYes: false,
    landRegisteredExpectedDate: "",
    propertyPurchasedYes: false,
    propertyPurchasedEOI: false,
    solicitorCompany: "ARROW WHITE PTY LTD",
    solicitorContact: "Yerin Yoo",
    solicitorPhone: "07 3051 0800",
    solicitorEmail: "y.yoo@arrowwhite.com.au",
    solicitorAddress: "PO Box 820, BRISBANE, QLD 4001",
    financeCompany: "ONE FINANCE AUSTRALIA",
    financeContact: "",
    financePhone: "",
    financeEmail: "",
    contractsImmediatelyYes: false,
    contractsImmediatelyNo: false,
    contractsSentToBuyerSolicitor: false,
    contractsSentToAgent: false,
    contractsViaDocuSign: false,
    contractsViaEmail: false,
    ownershipPercentClientA: "",
    ownershipPercentClientB: "",
    subjectToFinanceYes: false,
    subjectToFinanceNo: false,
    depositDueDate14Days: false,
    depositDueDate21Days: false,
    depositDueDate28Days: false,
    depositDueDateCustomDays: "",
    specialConditionsYes: false,
    specialConditionsNo: false,
    specialConditionsDetails: "",
  });

  const handleInputChange = (field: keyof EOIFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    toast({
      title: "Generating PDF",
      description: "Your fillable PDF is being generated...",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
    try {
      await generateEOIPDF(formData);
      toast({
        title: "PDF Downloaded",
        description: "Your fillable EOI PDF has been saved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("PDF generation error:", error);
      toast({
        title: "PDF Generation Failed",
        description: "There was an error generating the PDF. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const greyBg = "gray.100";
  const inputStyle = {
    bg: greyBg,
    border: "1px solid",
    borderColor: "gray.300",
    _hover: { borderColor: "gray.400" },
    _focus: { borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" },
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/eoi-form"
        fallbackTitle="EOI Form - Expression of Interest"
        fallbackDescription="Fill out the Expression of Interest Sales Information Sheet"
      />

      <Box className="no-print">
        <NavbarWithCallToAction />
      </Box>

      <Container maxW="container.xl" py={8}>
        {/* Action buttons - hide on print */}
        <HStack spacing={4} mb={6} className="no-print">
          <Button leftIcon={<FaPrint />} colorScheme="blue" onClick={handlePrint}>
            Print Form
          </Button>
          <Button leftIcon={<FaDownload />} colorScheme="green" onClick={handleDownloadPDF}>
            Download as PDF
          </Button>
        </HStack>

        {/* Printable Form */}
        <Box id="eoi-form-printable" bg="white" p={8} border="1px solid" borderColor="gray.200" borderRadius="md">
          {/* Header */}
          <HStack justify="space-between" mb={6} align="flex-start">
            <Box flexShrink={0}>
              <Image
                src="/assets/One-logo.png"
                alt="One Property Australia"
                h="80px"
                w="auto"
                objectFit="contain"
              />
            </Box>
            <Heading size="lg" textAlign="center" flex={1} alignSelf="center">
              EOI / SALES INFORMATION SHEET
            </Heading>
            <VStack align="flex-end" spacing={0} fontSize="xs" flexShrink={0}>
              <Text fontWeight="bold">One Property Australia</Text>
              <Text>ACN: 664 898 846</Text>
              <Text>6/22 Alexandra Pl,</Text>
              <Text>Murarrie QLD 4172</Text>
              <Text>ONEPROPERTYAUSTRALIA.COM.AU</Text>
            </VStack>
          </HStack>

          {/* Top Fields: Date, Sales Channel, BDM */}
          <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Date:</FormLabel>
              <Input
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Sales Channel:</FormLabel>
              <Input
                value={formData.salesChannel}
                onChange={(e) => handleInputChange("salesChannel", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">BDM:</FormLabel>
              <Input
                value={formData.bdm}
                onChange={(e) => handleInputChange("bdm", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Divider my={4} />

          {/* CLIENT DETAILS */}
          <Heading size="md" mb={4} fontWeight="bold">CLIENT DETAILS</Heading>

          {/* Client A */}
          <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">First Name (Client A)</FormLabel>
              <Input
                value={formData.firstNameClientA}
                onChange={(e) => handleInputChange("firstNameClientA", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Middle Name (Client A)</FormLabel>
              <Input
                value={formData.middleNameClientA}
                onChange={(e) => handleInputChange("middleNameClientA", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Last Name (Client A)</FormLabel>
              <Input
                value={formData.lastNameClientA}
                onChange={(e) => handleInputChange("lastNameClientA", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          {/* Client B */}
          <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">First Name (Client B)</FormLabel>
              <Input
                value={formData.firstNameClientB}
                onChange={(e) => handleInputChange("firstNameClientB", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Middle Name (Client B)</FormLabel>
              <Input
                value={formData.middleNameClientB}
                onChange={(e) => handleInputChange("middleNameClientB", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Last Name (Client B)</FormLabel>
              <Input
                value={formData.lastNameClientB}
                onChange={(e) => handleInputChange("lastNameClientB", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          {/* Address and Phone */}
          <Grid templateColumns="2fr 1fr 1fr" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Address:</FormLabel>
              <Input
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Phone (Primary):</FormLabel>
              <Input
                value={formData.phonePrimary}
                onChange={(e) => handleInputChange("phonePrimary", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Phone (Secondary):</FormLabel>
              <Input
                value={formData.phoneSecondary}
                onChange={(e) => handleInputChange("phoneSecondary", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          {/* Email */}
          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Email (Primary):</FormLabel>
              <Input
                type="email"
                value={formData.emailPrimary}
                onChange={(e) => handleInputChange("emailPrimary", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Email (Secondary):</FormLabel>
              <Input
                type="email"
                value={formData.emailSecondary}
                onChange={(e) => handleInputChange("emailSecondary", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          {/* Purchasing Entity */}
          <FormControl mb={4}>
            <FormLabel fontSize="sm">Purchasing Entity:</FormLabel>
            <Input
              value={formData.purchasingEntity}
              onChange={(e) => handleInputChange("purchasingEntity", e.target.value)}
              {...inputStyle}
              size="sm"
            />
          </FormControl>

          {/* Customer Type */}
          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="bold">Customer Type:</FormLabel>
            <HStack spacing={6}>
              <Checkbox
                isChecked={formData.customerTypeBuyer}
                onChange={(e) => handleInputChange("customerTypeBuyer", e.target.checked)}
              >
                Buyer
              </Checkbox>
              <Checkbox
                isChecked={formData.customerTypeOwnerOccupier}
                onChange={(e) => handleInputChange("customerTypeOwnerOccupier", e.target.checked)}
              >
                Owner Occupier
              </Checkbox>
              <Checkbox
                isChecked={formData.customerTypeInvestor}
                onChange={(e) => handleInputChange("customerTypeInvestor", e.target.checked)}
              >
                Investor
              </Checkbox>
              <Checkbox
                isChecked={formData.customerTypeSMSF}
                onChange={(e) => handleInputChange("customerTypeSMSF", e.target.checked)}
              >
                SMSF
              </Checkbox>
            </HStack>
          </FormControl>

          <Divider my={4} />

          {/* PROPERTY DETAILS */}
          <Heading size="md" mb={4} fontWeight="bold">PROPERTY DETAILS</Heading>

          <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Estate Name:</FormLabel>
              <Input
                value={formData.estateName}
                onChange={(e) => handleInputChange("estateName", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Stage:</FormLabel>
              <Input
                value={formData.stage}
                onChange={(e) => handleInputChange("stage", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Lot Number:</FormLabel>
              <Input
                value={formData.lotNumber}
                onChange={(e) => handleInputChange("lotNumber", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Street:</FormLabel>
              <Input
                value={formData.street}
                onChange={(e) => handleInputChange("street", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Grid templateColumns="1fr 1fr 2fr" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">House Design:</FormLabel>
              <Input
                value={formData.houseDesign}
                onChange={(e) => handleInputChange("houseDesign", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Facade:</FormLabel>
              <Input
                value={formData.facade}
                onChange={(e) => handleInputChange("facade", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Land Size:</FormLabel>
              <Input
                value={formData.landSize}
                onChange={(e) => handleInputChange("landSize", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Build $:</FormLabel>
              <Input
                value={formData.buildPrice}
                onChange={(e) => handleInputChange("buildPrice", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Land $:</FormLabel>
              <Input
                value={formData.landPrice}
                onChange={(e) => handleInputChange("landPrice", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Total $:</FormLabel>
              <Input
                value={formData.totalPrice}
                onChange={(e) => handleInputChange("totalPrice", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Land Registered:</FormLabel>
              <HStack spacing={6}>
                <Checkbox
                  isChecked={formData.landRegisteredYes}
                  onChange={(e) => handleInputChange("landRegisteredYes", e.target.checked)}
                >
                  Yes
                </Checkbox>
                <HStack spacing={2}>
                  <Text fontSize="sm">Expected:</Text>
                  <Input
                    value={formData.landRegisteredExpectedDate}
                    onChange={(e) => handleInputChange("landRegisteredExpectedDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    {...inputStyle}
                    size="sm"
                    width="120px"
                  />
                </HStack>
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Property Purchased:</FormLabel>
              <HStack spacing={6}>
                <Checkbox
                  isChecked={formData.propertyPurchasedYes}
                  onChange={(e) => handleInputChange("propertyPurchasedYes", e.target.checked)}
                >
                  Yes
                </Checkbox>
                <Checkbox
                  isChecked={formData.propertyPurchasedEOI}
                  onChange={(e) => handleInputChange("propertyPurchasedEOI", e.target.checked)}
                >
                  EOI
                </Checkbox>
              </HStack>
            </FormControl>
          </Grid>

          <Divider my={4} />

          {/* SOLICITOR & FINANCE */}
          <Heading size="md" mb={4} fontWeight="bold">SOLICITOR & FINANCE</Heading>

          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Solicitor Company:</FormLabel>
              <Input
                value={formData.solicitorCompany}
                onChange={(e) => handleInputChange("solicitorCompany", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Contact:</FormLabel>
              <Input
                value={formData.solicitorContact}
                onChange={(e) => handleInputChange("solicitorContact", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Phone:</FormLabel>
              <Input
                value={formData.solicitorPhone}
                onChange={(e) => handleInputChange("solicitorPhone", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Email:</FormLabel>
              <Input
                type="email"
                value={formData.solicitorEmail}
                onChange={(e) => handleInputChange("solicitorEmail", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <FormControl mb={4}>
            <FormLabel fontSize="sm">Address:</FormLabel>
            <Input
              value={formData.solicitorAddress}
              onChange={(e) => handleInputChange("solicitorAddress", e.target.value)}
              {...inputStyle}
              size="sm"
            />
          </FormControl>

          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Finance Company:</FormLabel>
              <Input
                value={formData.financeCompany}
                onChange={(e) => handleInputChange("financeCompany", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Contact:</FormLabel>
              <Input
                value={formData.financeContact}
                onChange={(e) => handleInputChange("financeContact", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Phone:</FormLabel>
              <Input
                value={formData.financePhone}
                onChange={(e) => handleInputChange("financePhone", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Email:</FormLabel>
              <Input
                type="email"
                value={formData.financeEmail}
                onChange={(e) => handleInputChange("financeEmail", e.target.value)}
                {...inputStyle}
                size="sm"
              />
            </FormControl>
          </Grid>

          <Divider my={4} />

          {/* CONTRACT DETAILS */}
          <Heading size="md" mb={4} fontWeight="bold">CONTRACT DETAILS</Heading>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="bold">
              Do you require contracts to be sent out immediately for signing?
            </FormLabel>
            <HStack spacing={6}>
              <Checkbox
                isChecked={formData.contractsImmediatelyYes}
                onChange={(e) => handleInputChange("contractsImmediatelyYes", e.target.checked)}
              >
                Yes
              </Checkbox>
              <Checkbox
                isChecked={formData.contractsImmediatelyNo}
                onChange={(e) => handleInputChange("contractsImmediatelyNo", e.target.checked)}
              >
                No
              </Checkbox>
            </HStack>
          </FormControl>

          <HStack mb={4} align="flex-start">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Contracts to be sent to:</FormLabel>
              <HStack spacing={6}>
                <Checkbox
                  isChecked={formData.contractsSentToBuyerSolicitor}
                  onChange={(e) => handleInputChange("contractsSentToBuyerSolicitor", e.target.checked)}
                >
                  Buyer's Solicitor
                </Checkbox>
                <Checkbox
                  isChecked={formData.contractsSentToAgent}
                  onChange={(e) => handleInputChange("contractsSentToAgent", e.target.checked)}
                >
                  Agent
                </Checkbox>
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Via:</FormLabel>
              <HStack spacing={6}>
                <Checkbox
                  isChecked={formData.contractsViaDocuSign}
                  onChange={(e) => handleInputChange("contractsViaDocuSign", e.target.checked)}
                >
                  DocuSign
                </Checkbox>
                <Checkbox
                  isChecked={formData.contractsViaEmail}
                  onChange={(e) => handleInputChange("contractsViaEmail", e.target.checked)}
                >
                  Email
                </Checkbox>
              </HStack>
            </FormControl>
          </HStack>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="bold">Ownership % Split:</FormLabel>
            <HStack>
              <Text fontSize="sm">(Client A)</Text>
              <Input
                value={formData.ownershipPercentClientA}
                onChange={(e) => handleInputChange("ownershipPercentClientA", e.target.value)}
                {...inputStyle}
                size="sm"
                width="80px"
                textAlign="center"
              />
              <Text fontSize="sm">% /</Text>
              <Input
                value={formData.ownershipPercentClientB}
                onChange={(e) => handleInputChange("ownershipPercentClientB", e.target.value)}
                {...inputStyle}
                size="sm"
                width="80px"
                textAlign="center"
              />
              <Text fontSize="sm">% (Client B)</Text>
            </HStack>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="bold">
              Are there any special conditions required by the purchaser?
            </FormLabel>
            <HStack spacing={6} mb={2}>
              <Checkbox
                isChecked={formData.specialConditionsYes}
                onChange={(e) => handleInputChange("specialConditionsYes", e.target.checked)}
              >
                Yes
              </Checkbox>
              <Checkbox
                isChecked={formData.specialConditionsNo}
                onChange={(e) => handleInputChange("specialConditionsNo", e.target.checked)}
              >
                No
              </Checkbox>
            </HStack>
            <Text fontSize="sm" mb={2}>If yes, please provide details below:</Text>
            <Textarea
              value={formData.specialConditionsDetails}
              onChange={(e) => handleInputChange("specialConditionsDetails", e.target.value)}
              {...inputStyle}
              rows={4}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="bold">Is this contract subject to finance?</FormLabel>
            <HStack spacing={6}>
              <Checkbox
                isChecked={formData.subjectToFinanceYes}
                onChange={(e) => handleInputChange("subjectToFinanceYes", e.target.checked)}
              >
                Yes
              </Checkbox>
              <Checkbox
                isChecked={formData.subjectToFinanceNo}
                onChange={(e) => handleInputChange("subjectToFinanceNo", e.target.checked)}
              >
                No
              </Checkbox>
            </HStack>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="bold">
              If not subject to finance - Please specify Deposit Due Date:
            </FormLabel>
            <HStack spacing={6} mb={2}>
              <Checkbox
                isChecked={formData.depositDueDate14Days}
                onChange={(e) => handleInputChange("depositDueDate14Days", e.target.checked)}
              >
                14 days
              </Checkbox>
              <Checkbox
                isChecked={formData.depositDueDate21Days}
                onChange={(e) => handleInputChange("depositDueDate21Days", e.target.checked)}
              >
                21 days
              </Checkbox>
              <Checkbox
                isChecked={formData.depositDueDate28Days}
                onChange={(e) => handleInputChange("depositDueDate28Days", e.target.checked)}
              >
                28 days
              </Checkbox>
            </HStack>
            <HStack>
              <Text fontSize="sm">How many days?</Text>
              <Input
                value={formData.depositDueDateCustomDays}
                onChange={(e) => handleInputChange("depositDueDateCustomDays", e.target.value)}
                {...inputStyle}
                size="sm"
                width="120px"
              />
            </HStack>
          </FormControl>

          <Box mt={6} p={4} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" mb={2}>Please Note:</Text>
            <Text fontSize="sm">
              The Solicitor Company mentioned above will be in touch to outline the EOI deposit amount and the Trust Account details for where you are to place the EOI deposit.
            </Text>
          </Box>
        </Box>
      </Container>

      <Box className="no-print">
        <FooterWithFourColumns />
      </Box>

      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              margin: 0;
              padding: 0;
            }
            #eoi-form-printable {
              border: none !important;
              box-shadow: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default EOIForm;
